import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Productivity = () => {
  // State variables for Productivity calculation
  const [output, setOutput] = useState(0); // Output (e.g., units produced)
  const [input, setInput] = useState(0); // Input (e.g., hours worked)
  const [productivity, setProductivity] = useState(0); // Calculated productivity

  // Event handlers to update state values
  const handleOutputChange = (e) => setOutput(parseFloat(e.target.value) || 0);
  const handleInputChange = (e) => setInput(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate productivity when any input changes
  useEffect(() => {
    if (output && input) {
      const productivityValue = output / input; // Calculate productivity
      setProductivity(productivityValue);
    } else {
      setProductivity(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [output, input]);

  return (
    <Layout title="Productivity Calculator" path="productivity">
      <section className="section-padding" id="productivity-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Productivity Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Productivity Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Output Input (e.g., units produced) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="output" className="form-label">
                        Output (e.g., units produced)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Output:</strong> Enter the total number of units produced or completed tasks during the given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="output"
                        placeholder="Enter output (units produced)"
                        value={output || ""}
                        onChange={handleOutputChange}
                      />
                    </div>

                    {/* Input Input (e.g., hours worked) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="input" className="form-label">
                        Input (e.g., hours worked)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Input:</strong> Enter the total number of hours worked or resources used to produce the output."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="input"
                        placeholder="Enter input (hours worked)"
                        value={input || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Productivity:{" "}
                        <span className="fw-bold ms-3">
                          {productivity === 0 ? "0.00" : productivity.toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Productivity Calculator
                </h3>

                {/* What is a Productivity Calculator? */}
                <h4 className="mt-3 mb-3">What is a Productivity Calculator?</h4>
                <p>
                  A **Productivity Calculator** measures how effectively resources, such as time or labor, are used to produce output. It calculates the ratio of output (e.g., units produced) to input (e.g., hours worked). This tool helps organizations assess their work efficiency and identify areas where improvements can be made.
                </p>

                {/* Why Use a Productivity Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Productivity Calculator?</h4>
                <p>
                  This calculator is useful for understanding how well employees, teams, or systems are performing relative to the resources spent. By analyzing productivity, companies can make informed decisions about workflow improvements, resource allocation, and workforce management.
                </p>

                {/* Benefits of Using a Productivity Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Productivity Calculator</h4>
                <ul>
                  <li><strong>Useful for optimizing employee performance:</strong> By identifying productivity trends, businesses can implement changes to enhance output.</li>
                  <li><strong>Identifies areas for productivity improvement:</strong> Helps identify bottlenecks or inefficient processes that hinder optimal performance.</li>
                  <li><strong>Helps companies track project progress:</strong> Monitoring productivity helps keep projects on track and within budget by ensuring efficient use of resources.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Productivity Calculator?</h4>
                <p>
                  The Productivity Calculator is ideal for:
                </p>
                <ul>
                  <li><strong>Manufacturing:</strong> To track production output and improve the efficiency of the production line.</li>
                  <li><strong>Service Industries:</strong> To evaluate how effectively service employees are completing tasks or serving customers.</li>
                  <li><strong>Project Management:</strong> To monitor the progress of a project and ensure that resources are being used efficiently.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What factors can affect productivity?</h4>
                <p>
                  Factors that affect productivity include the skills of employees, the quality of tools and equipment, the work environment, the clarity of project goals, and the effectiveness of management practices. By addressing these factors, organizations can increase overall productivity.
                </p>

                <h4 className="mt-4 mb-3">How can companies improve productivity?</h4>
                <p>
                  Companies can improve productivity by investing in employee training, upgrading tools and equipment, streamlining workflows, reducing distractions, and setting clear, measurable goals for workers. Additionally, offering incentives for high performance can also drive better results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Productivity;
